const STORAGE_KEY = "humanvszombieData"

/* What this browser is allowed to hand the store back, and in what shape.

   The stored object is read straight into the user slice (the tokens) and the ui slice (the city
   order), and for a year it was trusted as it came: `JSON.parse` succeeding was the whole check.
   A value written by an older build, or edited, or half-written when the tab was killed, then
   crashed the first reducer to read it — an app installed months ago opened to a black screen
   that a reload, or an incognito window with nothing stored, did not have (owner, 2026-09-24).
   So each key is checked for the shape its reader expects and dropped otherwise: a session that
   is not two strings is no session, and a city order that is not `{gameId: ["col-row", …]}` is
   nobody's preference. Nothing here is precious enough to be worth a crash. */
const isString = (value) => typeof value === "string" && value.length > 0
const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value)

const sanitizeCityOrder = (cityOrder) => {
  if (!isPlainObject(cityOrder)) return undefined
  const clean = {}
  Object.entries(cityOrder).forEach(([gameId, order]) => {
    if (Array.isArray(order) && order.every(isString)) clean[gameId] = order
  })
  return clean
}

// `{gameId: {"<userId>@<col-row>": angle}}`, an angle a finite number: what a camp faces.
const sanitizeStackFacing = (stackFacing) => {
  if (!isPlainObject(stackFacing)) return undefined
  const clean = {}
  Object.entries(stackFacing).forEach(([gameId, facings]) => {
    if (!isPlainObject(facings)) return
    const kept = Object.entries(facings).filter(([key, angle]) => isString(key) && Number.isFinite(angle))
    if (kept.length) clean[gameId] = Object.fromEntries(kept)
  })
  return clean
}

export const sanitizeStoredData = (data) => {
  if (!isPlainObject(data)) return {}
  const clean = {}
  if (isString(data.accessToken)) clean.accessToken = data.accessToken
  if (isString(data.refreshToken)) clean.refreshToken = data.refreshToken
  const cityOrder = sanitizeCityOrder(data.cityOrder)
  if (cityOrder) clean.cityOrder = cityOrder
  const stackFacing = sanitizeStackFacing(data.stackFacing)
  if (stackFacing) clean.stackFacing = stackFacing
  return clean
}

// Function to save data to localStorage
export const saveToLocalStorage = (data) => {
  try {
    const existingData = localStorage.getItem(STORAGE_KEY) || "{}"
    const existingDataJson = sanitizeStoredData(JSON.parse(existingData))
    const newDataJson = { ...existingDataJson, ...data }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newDataJson))
  } catch (error) {
    console.error("Error saving to localStorage:", error)
  }
}

// Function to load data from localStorage. Always an object: a store that cannot be read is an
// empty one, never `undefined` for a caller to trip over.
export const loadFromLocalStorage = () => {
  try {
    const serializedData = localStorage.getItem(STORAGE_KEY)
    if (serializedData === null) return {}
    return sanitizeStoredData(JSON.parse(serializedData))
  } catch (error) {
    console.error("Error loading from localStorage:", error)
    console.error("Restarting storage")
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (removeError) {
      // Storage the browser refuses to touch at all: nothing to clear.
    }
    return {}
  }
}

// Function to clear data from localStorage
export const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error("Error clearing localStorage:", error)
  }
}

export { STORAGE_KEY }
